import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Package, Upload, X, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    youtube_url: '',
    imageUrls: [''],
    tags: '',
    colors: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newUrl = response.data.url;
      // Add the new URL to the image list
      const updatedUrls = [...formData.imageUrls];
      // If the last one is empty, replace it
      if (updatedUrls.length > 0 && updatedUrls[updatedUrls.length - 1] === '') {
        updatedUrls[updatedUrls.length - 1] = newUrl;
      } else {
        updatedUrls.push(newUrl);
      }
      
      setFormData({ ...formData, imageUrls: updatedUrls });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const fetchProducts = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API}/products`,
        {
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price),
          category_id: formData.category_id,
          youtube_url: formData.youtube_url,
          images: formData.imageUrls.filter(url => url.trim() !== ''),
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
          colors: formData.colors.split(',').map(color => color.trim()).filter(color => color !== '')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Product added successfully!');
      setIsAddDialogOpen(false);
      setFormData({ name: '', description: '', price: '', category_id: '', youtube_url: '', imageUrls: [''], tags: '', colors: '' });
      fetchProducts();
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('token');

    try {
      await axios.put(
        `${API}/products/${editingProduct.id}`,
        {
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price),
          category_id: formData.category_id,
          youtube_url: formData.youtube_url,
          images: formData.imageUrls.filter(url => url.trim() !== ''),
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
          colors: formData.colors.split(',').map(color => color.trim()).filter(color => color !== '')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Product updated successfully!');
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    const token = sessionStorage.getItem('token');
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      youtube_url: product.youtube_url || '',
      imageUrls: product.images || [''],
      tags: (product.tags || []).join(', '),
      colors: (product.colors || []).join(', ')
    });
    setIsEditDialogOpen(true);
  };

  const addImageUrlField = () => {
    setFormData({ ...formData, imageUrls: [...formData.imageUrls, ''] });
  };

  const updateImageUrl = (index, value) => {
    const newUrls = [...formData.imageUrls];
    newUrls[index] = value;
    setFormData({ ...formData, imageUrls: newUrls });
  };

  const removeImageUrl = (index) => {
    const newUrls = formData.imageUrls.filter((_, i) => i !== index);
    setFormData({ ...formData, imageUrls: newUrls.length ? newUrls : [''] });
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="admin-products-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="products-title">Manage Products</h1>
              <p className="text-muted-foreground">Add, edit, or remove products</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="rounded-full bg-accent hover:bg-accent/90" data-testid="manage-categories-button">
                <Link to="/admin/categories">
                  <LayoutGrid className="mr-2 h-5 w-5" />
                  Manage Categories
                </Link>
              </Button>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="add-product-button">
                    <Plus className="mr-2 h-5 w-5" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Product</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddProduct} className="space-y-4" data-testid="add-product-form">
                    <div className="space-y-2">
                      <Label htmlFor="name">Product Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-white/5 border-white/10"
                        required
                        data-testid="product-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="bg-white/5 border-white/10"
                        rows={3}
                        required
                        data-testid="product-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price">Price (₹)</Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          className="bg-white/5 border-white/10"
                          required
                          data-testid="product-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                          <SelectTrigger className="bg-white/5 border-white/10" data-testid="product-category-select">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="youtube">YouTube URL (optional)</Label>
                      <Input
                        id="youtube"
                        value={formData.youtube_url}
                        onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                        className="bg-white/5 border-white/10"
                        placeholder="https://www.youtube.com/watch?v=..."
                        data-testid="product-youtube-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags (comma separated)</Label>
                      <Input
                        id="tags"
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        className="bg-white/5 border-white/10"
                        placeholder="3dprint, custom, art"
                        data-testid="product-tags-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="colors">Available Colours (comma separated)</Label>
                      <Input
                        id="colors"
                        value={formData.colors}
                        onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                        className="bg-white/5 border-white/10"
                        placeholder="Red, Blue, Green, Multi-color"
                        data-testid="product-colors-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <Label>Product Images (URLs)</Label>
                        <div className="flex flex-wrap gap-2">
                          <label className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 px-3 py-2 variant-ghost">
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                            <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                          </label>
                          <Button type="button" onClick={addImageUrlField} variant="ghost" size="sm" data-testid="add-image-url-button">
                            <Plus className="h-4 w-4 mr-1" />
                            Add URL
                          </Button>
                        </div>
                      </div>
                      {uploading && <p className="text-xs text-primary text-right">Uploading...</p>}
                      {formData.imageUrls.map((url, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={url}
                            onChange={(e) => updateImageUrl(index, e.target.value)}
                            className="bg-white/5 border-white/10"
                            placeholder="https://example.com/image.jpg"
                            data-testid={`image-url-input-${index}`}
                          />
                          {formData.imageUrls.length > 1 && (
                            <Button type="button" onClick={() => removeImageUrl(index)} variant="ghost" size="icon" data-testid={`remove-image-${index}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="submit" className="w-full rounded-full bg-primary" data-testid="submit-product-button">
                      Add Product
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="glass-morphic rounded-2xl overflow-hidden"
                  data-testid={`product-${product.id}`}
                >
                  <div className="aspect-square bg-muted/20 overflow-hidden cursor-zoom-in">
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={getImageUrl(product.images[0])} 
                        alt={product.name} 
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" 
                        onClick={() => setImageModal({
                          isOpen: true,
                          imageUrl: getImageUrl(product.images[0]),
                          productName: product.name,
                        })}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-medium" data-testid={`product-name-${product.id}`}>{product.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                    <p className="text-2xl font-bold text-primary" data-testid={`product-price-${product.id}`}>₹{product.price.toFixed(2)}</p>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={() => openEditDialog(product)} variant="outline" className="flex-1" data-testid={`edit-product-${product.id}`}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button onClick={() => handleDeleteProduct(product.id)} variant="outline" className="text-destructive hover:bg-destructive/10" data-testid={`delete-product-${product.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-products">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-xl text-muted-foreground">No products yet. Add your first product!</p>
            </div>
          )}
        </motion.div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProduct} className="space-y-4" data-testid="edit-product-form">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Product Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10"
                required
                data-testid="edit-product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10"
                rows={3}
                required
                data-testid="edit-product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-price">Price (₹)</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="bg-white/5 border-white/10"
                  required
                  data-testid="edit-product-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="edit-product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-youtube">YouTube URL</Label>
              <Input
                id="edit-youtube"
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="edit-product-youtube-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tags">Tags (comma separated)</Label>
              <Input
                id="edit-tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="bg-white/5 border-white/10"
                placeholder="3dprint, custom, art"
                data-testid="edit-product-tags-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-colors">Available Colours (comma separated)</Label>
              <Input
                id="edit-colors"
                value={formData.colors}
                onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                className="bg-white/5 border-white/10"
                placeholder="Red, Blue, Green, Multi-color"
                data-testid="edit-product-colors-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Product Images (URLs)</Label>
                <div className="flex gap-2">
                  <label className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 px-3 py-2 variant-ghost">
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                  <Button type="button" onClick={addImageUrlField} variant="ghost" size="sm" data-testid="edit-add-image-url-button">
                    <Plus className="h-4 w-4 mr-1" />
                    Add URL
                  </Button>
                </div>
              </div>
              {uploading && <p className="text-xs text-primary text-right">Uploading...</p>}
              {formData.imageUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={url}
                    onChange={(e) => updateImageUrl(index, e.target.value)}
                    className="bg-white/5 border-white/10"
                    placeholder="https://example.com/image.jpg"
                    data-testid={`edit-image-url-input-${index}`}
                  />
                  {formData.imageUrls.length > 1 && (
                    <Button type="button" onClick={() => removeImageUrl(index)} variant="ghost" size="icon" data-testid={`edit-remove-image-${index}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full rounded-full bg-primary" data-testid="update-product-button">
              Update Product
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        productName={imageModal.productName}
      />
    </div>
  );
}
